import React, { useState } from 'react';
import { Button, ActionSheet, Toast } from 'antd-mobile';
import { Action } from 'antd-mobile/es/components/action-sheet';
import { t } from 'i18n';
import PickUpPhotoService from "../services/PickUpPhotoService";
import {isPickUpNativeBridgeAvailable, PickUpImageCompressionOptions} from "./PickUpPhotoNativeBridge";

// 简单的平台检测函数
const isIOS = (): boolean => {
  return window?.webkit?.messageHandlers !== undefined;
};

interface PickUpPhotoSelectorProps {
  onPhotoSelected: (file: File) => void;
  buttonText?: string | React.ReactNode;
  buttonProps?: any;
  children?: React.ReactNode;
  showActionSheet?: boolean;
  disabled?: boolean;
  compressionOptions?: PickUpImageCompressionOptions; // 图片压缩选项
  /** 仅允许拍照，禁止从相册选取 */
  cameraOnly?: boolean;
}

const PickUpPhotoSelector: React.FC<PickUpPhotoSelectorProps> = ({
  onPhotoSelected,
  buttonText,
  buttonProps,
  showActionSheet,
  disabled = false,
  compressionOptions,
  cameraOnly = false,
}) => {
  const defaultShowActionSheet = showActionSheet ?? (isIOS() ? false : true);
  const [loading, setLoading] = useState(false);
  const [actionSheetVisible, setActionSheetVisible] = useState(false);

  const handleTakePhoto = async (useFrontCamera: boolean = false) => {
    setLoading(true);
    try {
      const file = await PickUpPhotoService.takePhoto(useFrontCamera, compressionOptions);
      onPhotoSelected(file);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      // 用户主动取消拍照，静默处理不提示
      if (errorMessage.toLowerCase().includes('cancel')) {
        return;
      }
      console.error('Photo capture failed:', error);
      Toast.show({ content: t('pages.pickUp.photo.uploadFailed'), position: 'bottom' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFromGallery = async () => {
    setLoading(true);
    try {
      const file = await PickUpPhotoService.selectFromGallery(compressionOptions);
      onPhotoSelected(file);
    } catch (error) {
      console.error('Photo selection failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      Toast.show({ 
        content: t('pages.pickUp.photo.selectionFailed', { errorMessage }), 
        position: 'bottom' 
      });
    } finally {
      setLoading(false);
    }
  };

  const showPhotoOptions = () => {
    // cameraOnly 仅在原生桥接可用时生效（APP 环境）；非原生环境降级为普通流程
    if (cameraOnly && isPickUpNativeBridgeAvailable()) {
      handleTakePhoto(false);
      return;
    }
    if (defaultShowActionSheet) {
      setActionSheetVisible(true);
    } else {
      // 如果不显示ActionSheet，默认从相册选择
      handleSelectFromGallery();
    }
  };

  // 根据是否有原生桥接来决定显示哪些选项
  const getActions = (): Action[] => {
    const actions: Action[] = [];

    console.log('isNativeBridgeAvailable', isPickUpNativeBridgeAvailable());

    if (isPickUpNativeBridgeAvailable()) {
      actions.push(
        {
          text: t('pages.delivery.detail.completeTakePhoto'),
          key: 'camera',
          onClick: () => {
            setActionSheetVisible(false);
            handleTakePhoto(false);
          },
        },
      );
    }

    actions.push(
      {
        text: t('pages.delivery.detail.completeTakeSelect'),
        key: 'gallery',
        onClick: () => {
          setActionSheetVisible(false);
          handleSelectFromGallery();
        },
      },
      {
        text: t('pages.delivery.detail.cancel'),
        key: 'cancel',
        onClick: () => {
          setActionSheetVisible(false);
        },
      }
    );

    return actions;
  };

  return (
    <>
      <Button
        onClick={showPhotoOptions}
        loading={loading}
        block
        fill="none"
        disabled={disabled}
        {...buttonProps}
      >
        {buttonText}
      </Button>
      {defaultShowActionSheet && (
        <ActionSheet
          visible={actionSheetVisible}
          actions={getActions()}
          onClose={() => setActionSheetVisible(false)}
        />
      )}
    </>
  );
};

export default PickUpPhotoSelector;